/**
 * Timeline Slider — linha do tempo com navegação por página e trilha clicável.
 * Conteúdo vem de JSON (data-timeline-slider), renderizado dinamicamente.
 * Desktop: 2 itens por página. Mobile (<768px): 1 item por página.
 */

const BREAKPOINT = '(min-width: 768px)';
const SLIDE_MS = 300;

export function initTimelineSlider() {
  const roots = document.querySelectorAll('[data-timeline-slider]');
  if (!roots.length) return;

  roots.forEach(setupTimeline);
}

function setupTimeline(root) {
  let items = [];
  try {
    items = JSON.parse(root.getAttribute('data-timeline-slider') || '[]');
  } catch (err) {
    console.error('Timeline slider: JSON inválido.', err);
    return;
  }
  if (!items.length) return;

  const dots = root.querySelectorAll('[data-timeline-dot]');
  const miniTrack = root.querySelector('[data-timeline-mini-track]');
  const content = root.querySelector('[data-timeline-content]');
  const bloco = root.querySelector('[data-timeline-bloco]');
  const prevBtns = root.querySelectorAll('[data-timeline-prev]');
  const nextBtns = root.querySelectorAll('[data-timeline-next]');

  const mq = window.matchMedia(BREAKPOINT);
  let pageStart = 0;

  const pageSize = () => (mq.matches ? 2 : 1);

  const clampStart = (start, size) => {
    const maxStart = Math.max(0, items.length - size);
    return Math.min(Math.max(start, 0), maxStart);
  };

  function renderTrack(size) {
    dots.forEach((dot) => {
      const index = parseInt(dot.dataset.index, 10);
      dot.classList.toggle('is-active', index >= pageStart && index < pageStart + size);
    });
  }

  function renderMiniTrack(pageItems, size) {
    if (size >= 2) {
      // Grid espelhando as colunas do conteúdo, pra cada dot alinhar com o card correspondente.
      miniTrack.classList.add('timeline-slider__mini-track--grid');
      miniTrack.innerHTML = pageItems
        .map(() => '<div class="timeline-slider__mini-item"><span class="timeline-slider__mini-dot"></span></div>')
        .join('');
      return;
    }

    miniTrack.classList.remove('timeline-slider__mini-track--grid');
    const next = items[pageStart + 1];
    miniTrack.innerHTML = `
      <span class="timeline-slider__mini-dot"></span>
      <span class="timeline-slider__mini-line"></span>
      ${next ? `<span class="timeline-slider__mini-label">${escapeHtml(next.ano)}</span>` : ''}
    `;
  }

  function renderContent(pageItems) {
    content.innerHTML = pageItems
      .map(
        (item) => `
          <div class="timeline-slider__card">
            <h3 class="timeline-slider__card-ano">${escapeHtml(item.ano)}</h3>
            <div class="timeline-slider__card-texto">${(item.paragrafos || []).join('')}</div>
          </div>
        `
      )
      .join('');
  }

  function renderBloco(pageItems) {
    const primary = pageItems[0] || {};
    if (!primary.blocoTitulo && !primary.blocoDescricao) {
      bloco.innerHTML = '';
      return;
    }
    bloco.innerHTML = `
      ${primary.blocoTitulo ? `<h3 class="timeline-slider__bloco-titulo">${escapeHtml(primary.blocoTitulo)}</h3>` : ''}
      ${primary.blocoDescricao ? `<div class="timeline-slider__bloco-texto">${primary.blocoDescricao}</div>` : ''}
    `;
  }

  function renderNavState(size) {
    const atStart = pageStart === 0;
    const atEnd = pageStart + size >= items.length;
    prevBtns.forEach((btn) => { btn.disabled = atStart; });
    nextBtns.forEach((btn) => { btn.disabled = atEnd; });
  }

  function render() {
    const size = pageSize();
    pageStart = clampStart(pageStart, size);
    const pageItems = items.slice(pageStart, pageStart + size);

    renderTrack(size);
    renderMiniTrack(pageItems, size);
    renderContent(pageItems);
    renderBloco(pageItems);
    renderNavState(size);
  }

  function renderWithSlide(direction) {
    const els = [miniTrack, content, bloco];
    const outClass = direction > 0 ? 'is-slide-out-next' : 'is-slide-out-prev';
    const inClass = direction > 0 ? 'is-slide-in-next' : 'is-slide-in-prev';

    els.forEach((el) => el.classList.add(outClass));

    window.setTimeout(() => {
      render();

      els.forEach((el) => el.classList.remove(outClass));
      els.forEach((el) => el.classList.add(inClass));
      // força reflow para o navegador registrar o estado "in" antes de removê-lo
      void els[0].offsetWidth;
      els.forEach((el) => el.classList.remove(inClass));
    }, SLIDE_MS);
  }

  function goTo(start) {
    const size = pageSize();
    const target = clampStart(start, size);
    if (target === pageStart) return;
    const direction = target > pageStart ? 1 : -1;
    pageStart = target;
    renderWithSlide(direction);
  }

  prevBtns.forEach((btn) => btn.addEventListener('click', () => goTo(pageStart - pageSize())));
  nextBtns.forEach((btn) => btn.addEventListener('click', () => goTo(pageStart + pageSize())));

  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      goTo(parseInt(dot.dataset.index, 10));
    });
  });

  mq.addEventListener('change', () => {
    pageStart = clampStart(pageStart, pageSize());
    render();
  });

  render();
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str ?? '';
  return div.innerHTML;
}
